import React from 'react';
import useIntl from 'react-intl/src/components/useIntl';
import { Form, Input, Message } from 'semantic-ui-react';
import { CustomNumberInput } from 'src/components/CustomNumberInput';
import messages from 'src/messages';
import {
  MAX_PRECISION,
  type FieldMessage,
  type KindSettingsProps,
} from './fieldDefinitions';

/** Number inputs are held as text so a part-typed value isn't rewritten. */
const asText = (next: any) => (next == null || next === '' ? '' : String(next));

/** Precision, default and the optional min/max bounds of a number field. */
export function NumberFieldSettings({
  draft,
  errors,
  onChange,
}: KindSettingsProps) {
  const { formatMessage } = useIntl();
  const { precision, defaultText, minVal, maxVal } = draft;
  const decimals = Number(precision) || 0;

  return (
    <>
      <Form.Group widths="equal">
        <Form.Field>
          <label>{formatMessage(messages.customFieldPrecision)}</label>
          <Input
            type="number"
            min={0}
            max={MAX_PRECISION}
            step={1}
            value={precision}
            onChange={(e) => onChange({ precision: e.target.value })}
          />
        </Form.Field>
        <Form.Field>
          <label>{formatMessage(messages.customFieldDefaultValue)}</label>
          <CustomNumberInput
            value={defaultText === '' ? null : defaultText}
            precision={decimals}
            min={minVal !== '' ? Number(minVal) : null}
            max={maxVal !== '' ? Number(maxVal) : null}
            onChange={(next) => onChange({ defaultText: asText(next) })}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field>
          <label>{formatMessage(messages.customFieldMinimum)}</label>
          <CustomNumberInput
            value={minVal === '' ? null : minVal}
            precision={decimals}
            placeholder={formatMessage(messages.customFieldNone)}
            onChange={(next) => onChange({ minVal: asText(next) })}
          />
        </Form.Field>
        <Form.Field>
          <label>{formatMessage(messages.customFieldMaximum)}</label>
          <CustomNumberInput
            value={maxVal === '' ? null : maxVal}
            precision={decimals}
            placeholder={formatMessage(messages.customFieldNone)}
            onChange={(next) => onChange({ maxVal: asText(next) })}
          />
        </Form.Field>
      </Form.Group>
      <NumberFieldErrors errors={errors} />
    </>
  );
}

function NumberFieldErrors({ errors }: { errors: FieldMessage[] }) {
  const { formatMessage } = useIntl();

  if (errors.length === 0) return null;

  return (
    <Message negative>
      <Message.List>
        {errors.map(({ message, values }) => (
          <Message.Item key={message.id}>
            {formatMessage(message, values)}
          </Message.Item>
        ))}
      </Message.List>
    </Message>
  );
}
